import { useEffect, useId, useRef, useState } from "react";
import { convertFlatToTree } from "../../../../utils/DataUtilities";
import BlockTitle from "../_BlockTitle";
import quickLinkStyles from "../../SchoolBlocks/QuickLinksBlock/styles/QuicklinkBlock.module.scss";
import styles from "./styles/LinksTerrace.module.scss";
import classNames from "classnames";
import { QuicklinkEditTree } from "../../SchoolBlocks/QuickLinksBlock/QuicklinkEditTree";
import { observer } from "mobx-react";
import ApiClient from "../../../../backends/ApiClient";
import NotificationManager from "../../../notifications/NotificationManager";
import { EditDropDownItem } from "../EditDropDown";
import FontAwesome from "../../../utilities/FontAwesome";
import { Card, Flex, ScrollArea, Text } from "@radix-ui/themes";
import { reportError } from "../../../../utils/errorReporting";
import NavigationLink from "../../../header/navigation/NavigationLink";
import { isFolder } from "../../../modals/AdminModal/utilities";
import { unescapeHtml } from "../../../../utils/StringUtilities";
import { useT } from "../../../utilities/TranslatedText";
import {
  ScrollHint,
  scrollRowClassName,
  useScrollButtons,
} from "../ScrollHint";

type LinkNode = Navigation & { children?: Navigation[] };

function LinkIcon(props: {
  link: Navigation;
  fallbackIcon: string;
  className?: string;
}) {
  return (
    <Flex
      asChild
      align={"center"}
      justify={"center"}
      flexShrink={"0"}
      className={props.className ?? styles.iconChip}
    >
      <span>
        <FontAwesome fixedWidth name={props.link.icon || props.fallbackIcon} />
      </span>
    </Flex>
  );
}

function FolderSummary(props: { folder: LinkNode }) {
  const t = useT();
  const count = props.folder.children?.length ?? 0;

  return (
    <Flex align={"center"} gap={"3"}>
      <LinkIcon link={props.folder} fallbackIcon={"fas fa-folder"} />
      <Flex direction={"column"} align={"start"}>
        <Text size={"3"} weight={"bold"} className={styles.cardTitle}>
          {unescapeHtml(props.folder.title)}
        </Text>
        <Text size={"1"} weight={"medium"} className={styles.count}>
          {count === 1 ? t("1 link") : t("{{count}} links", { count })}
        </Text>
      </Flex>
    </Flex>
  );
}

const HOVER_CLOSE_DELAY_MS = 150;

function FolderCard(props: { folder: LinkNode }) {
  const t = useT();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const lastPointerType = useRef("");
  const skipNextFocusOpen = useRef(false);

  function openNow() {
    window.clearTimeout(closeTimer.current);
    setOpen(true);
  }

  // the delay keeps a brief slip of the pointer from collapsing the card
  function closeSoon() {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(
      () => setOpen(false),
      HOVER_CLOSE_DELAY_MS,
    );
  }

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  return (
    <Card
      size={"2"}
      className={classNames(styles.card, styles.folderCard)}
      onPointerEnter={(event) => event.pointerType !== "touch" && openNow()}
      onPointerLeave={(event) => event.pointerType !== "touch" && closeSoon()}
      onFocus={() => {
        if (skipNextFocusOpen.current) {
          skipNextFocusOpen.current = false;
          return;
        }
        openNow();
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open) return;
        setOpen(false);
        skipNextFocusOpen.current = true;
        buttonRef.current?.focus();
      }}
    >
      <span className={styles.accentBar} />
      <button
        type={"button"}
        ref={buttonRef}
        aria-expanded={open}
        aria-controls={listId}
        className={styles.folderButton}
        onPointerDown={(event) => {
          lastPointerType.current = event.pointerType;
        }}
        onClick={() => {
          // hover and focus open the folder; only a tap toggles it
          if (lastPointerType.current === "touch") setOpen((value) => !value);
          lastPointerType.current = "";
        }}
      >
        <FolderSummary folder={props.folder} />
      </button>
      {open && (
        <ScrollArea
          type={"auto"}
          scrollbars={"vertical"}
          id={listId}
          className={styles.linkScroll}
          tabIndex={0}
          role={"region"}
          aria-label={t("{{name}} links", {
            name: unescapeHtml(props.folder.title),
          })}
        >
          <ul className={styles.linkList}>
            {(props.folder.children ?? []).map((link) => (
              <li key={link.id}>
                <Flex
                  asChild
                  align={"center"}
                  gap={"3"}
                  px={"2"}
                  py={"1"}
                  className={styles.link}
                >
                  <NavigationLink
                    href={link.url || link.external_url || ""}
                    navigationObj={link}
                  >
                    <LinkIcon
                      link={link}
                      fallbackIcon={"far fa-circle"}
                      className={styles.linkIconChip}
                    />
                    <Text size={"2"} weight={"medium"}>
                      {unescapeHtml(link.title)}
                    </Text>
                  </NavigationLink>
                </Flex>
              </li>
            ))}
          </ul>
        </ScrollArea>
      )}
    </Card>
  );
}

function LinkCard(props: { link: LinkNode }) {
  return (
    <Card
      asChild
      size={"2"}
      className={classNames(styles.card, styles.linkCard)}
    >
      <NavigationLink
        href={props.link.url || props.link.external_url || ""}
        navigationObj={props.link}
      >
        <span className={styles.accentBar} />
        <Flex align={"center"} gap={"3"} flexGrow={"1"}>
          <LinkIcon link={props.link} fallbackIcon={"far fa-circle"} />
          <Text size={"3"} weight={"bold"} className={styles.cardTitle}>
            {unescapeHtml(props.link.title)}
          </Text>
          <FontAwesome
            prefix={"fas"}
            name={"fa-arrow-right"}
            className={styles.cardArrow}
          />
        </Flex>
      </NavigationLink>
    </Card>
  );
}

const LinksTerrace = observer(
  (props: { blockObj: ILinksTerrace; aboveTheFold: boolean }) => {
    const [editing, setEditing] = useState(false);
    const scroll = useScrollButtons();

    async function handleCreateNavigation(navigation) {
      try {
        const client = await ApiClient();
        const newNav = await (client.POST(
          "/organizations/{organization_pk}/terraces/{terrace_pk}/navigation/",
          {
            params: {
              path: {
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
              },
            },
            body: {
              title: navigation.title,
              type: navigation.type,
              external_url: navigation.external_url,
              icon: navigation.icon,
              class_field: navigation.class_field,
              description: navigation.description,
              path: navigation.path,
              enabled: navigation.enabled,
              terrace: navigation.terrace,
              organization_id: navigation.organization_id,
              keep_site_context: navigation.keep_site_context,
            } as never,
          },
        ) as any);
        props.blockObj.links = [...props.blockObj.links, newNav];
        return newNav;
      } catch (e) {
        reportError(e, { error_source: "handled" });
        NotificationManager.error(e.message);
        return null;
      }
    }

    async function handleUpdateNavigations(navigations, uiOnly) {
      try {
        if (uiOnly) {
          // this is used when expanding a navigation when we don't actually need to update anything
          props.blockObj.links = navigations;
        } else {
          const client = await ApiClient();
          const newNavigations = await (client.PUT(
            "/organizations/{organization_pk}/terraces/{terrace_pk}/navigation/bulk_update/",
            {
              params: {
                path: {
                  organization_pk: props.blockObj.organization_id,
                  terrace_pk: props.blockObj.id,
                },
              },
              body: navigations,
            },
          ) as any);
          props.blockObj.links = newNavigations.map((newNav) => {
            const currentNav = props.blockObj.links.find(
              (nav) => nav.id === newNav.id,
            );
            if (currentNav) {
              newNav.expanded = currentNav.expanded;
            }
            return newNav;
          });
          NotificationManager.success("Links successfully updated!");
        }
      } catch (e) {
        reportError(e, { error_source: "handled" });
        NotificationManager.error(e.message);
      }
    }

    async function handleDeleteNavigations(navigations) {
      try {
        const navigationIds = navigations.map((n) => n.id);
        const client = await ApiClient();
        // the spec documents no requestBody for this endpoint, but at
        // runtime the app has always sent the array of ids to delete (SBSB-2912)
        await client.DELETE(
          "/organizations/{organization_pk}/terraces/{terrace_pk}/navigation/bulk_destroy/",
          {
            params: {
              path: {
                organization_pk: props.blockObj.organization_id,
                terrace_pk: props.blockObj.id,
              },
            },
            body: navigationIds as never,
          },
        );
        props.blockObj.links = props.blockObj.links.filter(
          (n) => !navigationIds.includes(n.id),
        );
      } catch (e) {
        reportError(e, { error_source: "handled" });
        NotificationManager.error(e.message);
      }
    }

    async function updateNavigation(navigation) {
      const client = await ApiClient();
      const newNav = await (client.PATCH(
        "/organizations/{organization_pk}/terraces/{terrace_pk}/navigation/{id}/",
        {
          params: {
            path: {
              organization_pk: props.blockObj.organization_id,
              terrace_pk: props.blockObj.id,
              id: navigation.id,
            },
          },
          body: navigation,
        },
      ) as any);
      props.blockObj.links = props.blockObj.links.map((nav) => {
        if (nav.id === newNav.id) {
          return newNav;
        }
        return nav;
      });
    }

    // convertFlatToTree, here and in QuicklinkEditTree, writes `children` onto the
    // observable links. Copying keeps this call's writes off them, and skipping it
    // while editing keeps the copy from tracking the editor's writes; either write
    // landing on something this observer reads re-renders it forever.
    const treeData = editing
      ? []
      : convertFlatToTree<LinkNode>(
          props.blockObj.links.map((link) => ({ ...link })),
        );

    function _setEditing(value) {
      setEditing(value);
    }

    const linksTerraceDropDownItems: EditDropDownItem[] = [
      {
        title: editing ? "Close Editor" : "Add / Edit Links",
        icon: (
          <FontAwesome prefix={"fas"} fixedWidth className="fa-pencil-alt" />
        ),
        onClick: () => _setEditing(!editing),
      },
    ];

    return (
      <article>
        <BlockTitle
          blockObj={props.blockObj}
          aboveTheFold={props.aboveTheFold}
          dropDownItems={linksTerraceDropDownItems}
          allowSave={true}
          showViewMore={false}
          rightElement={
            !editing && scroll.overflows ? (
              <ScrollHint scroll={scroll}>Scroll for more</ScrollHint>
            ) : undefined
          }
        />
        {editing ? (
          <div className={quickLinkStyles.blockList}>
            <QuicklinkEditTree
              links={props.blockObj.links}
              createNavigation={handleCreateNavigation}
              updateNavigations={handleUpdateNavigations}
              deleteNavigations={handleDeleteNavigations}
              updateNavigation={updateNavigation}
              allowSubFolders={false}
              terrace_id={props.blockObj.id}
            />
          </div>
        ) : (
          <Flex
            ref={scroll.rowRef}
            align={"start"}
            gap={"4"}
            px={"1"}
            pt={"3"}
            pb={"5"}
            className={scrollRowClassName}
            onScroll={scroll.onScroll}
          >
            {treeData.map((node) =>
              isFolder(node) ? (
                <FolderCard key={node.id} folder={node} />
              ) : (
                <LinkCard key={node.id} link={node} />
              ),
            )}
          </Flex>
        )}
      </article>
    );
  },
);

export default LinksTerrace;
